import sanitize from "sanitize-filename";
import { slugify } from "transliteration";

/**
 * Returns the lowercased file extension (with leading dot), or null if the
 * filename has no dot at all.
 */
export const getFileExtension = (filename: string): string | null => {
  if (!filename || typeof filename !== "string") {
    return null;
  }

  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1) {
    return null;
  }

  return filename.slice(lastDotIndex).toLowerCase();
};

/**
 * Sanitizes a filename by applying sanitize-filename library and custom normalization.
 * - Ref: https://github.com/supabase/supabase/issues/34595
 */
export const sanitizeFilename = (filename: string): string => {
  if (!filename || typeof filename !== "string") {
    return filename;
  }

  const slugified = slugify(filename, {
    lowercase: false,
    separator: " ",
    trim: true,
    // Note: `allowedChars` is a regex character class string (without `[]`).
    allowedChars: "a-zA-Z0-9_.~'() !*;:@&=+$,/?%#\\[\\]-",
  });

  let sanitized = sanitize(slugified);
  sanitized = sanitized
    // Replace Unicode whitespace (narrow no-break space, non-breaking space, etc.) with regular space
    .replace(/\p{White_Space}+/gu, " ")
    // Normalize special characters (ä -> a, é -> e, etc.)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    // Replace illegal characters with underscores
    .replace(/[!*';:@&=+$,/?%#[\]]/g, "_")
    // Remove consecutive underscores
    .replace(/_+/g, "_")
    // Trim leading/trailing underscores
    .replace(/^_+|_+$/g, "")
    // Ensure length is within limits
    .substring(0, 255);

  return sanitized;
};
